import { Container } from '@/ui/design-system/Container'
import { Button } from '@/ui/components'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { useRef } from 'react'
import { ProBadge } from '../ProBadge'
import { SharingPermissionsDemo } from './SharingPermissionsDemo'

const COLLAB_POINTS = [
  {
    title: 'A friends list built for crews',
    body: 'Keep your overlanding circle in one place and invite them to a trip in two taps. No phone-number roulette.'
  },
  {
    title: 'Revoke anytime',
    body: "Audience changed? Kill the link. Your spots stay yours."
  }
]

export function CollaborationSpotlight() {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 })
  const reducedMotion = useReducedMotion()
  const inView = reducedMotion || isInView

  const fadeInLeft = {
    hidden: reducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] as const }
    }
  }

  const fadeInRight = {
    hidden: reducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] as const }
    }
  }

  return (
    <section id="collaboration" ref={sectionRef} className="py-20 lg:py-40 bg-white scroll-mt-16">
      <Container size="app">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Side */}
          <motion.div
            className="flex flex-col gap-6"
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={fadeInLeft}
          >
            <p className="text-sm uppercase tracking-[0.3em] text-adventure-600 font-semibold">
              Why SitRepp
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
              Trips are better with a crew: share on your terms
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Getting a crew aligned without giving away your hard-won spots is the part no one else solves. Every share link has its own permission set. Toggle what guests can see.
            </p>

            <ul className="space-y-5">
              {COLLAB_POINTS.map((point, idx) => (
                <motion.li
                  key={idx}
                  className="flex items-start gap-3"
                  initial={reducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  transition={{ duration: 0.4, delay: 0.3 + (idx * 0.1), ease: [0.6, -0.05, 0.01, 0.99] as const }}
                >
                  <div className="mt-2 w-1.5 h-1.5 rounded-full bg-adventure-500 flex-shrink-0" />
                  <div>
                    <p className="text-base font-semibold text-foreground">{point.title}</p>
                    <p className="text-base text-earth-700">{point.body}</p>
                  </div>
                </motion.li>
              ))}
            </ul>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ProBadge />
              <span>Per-link controls are Pro. Basic sharing is free.</span>
            </div>

            <div className="pt-2">
              <Button asChild size="lg">
                <a href="/signup">Start planning with your crew</a>
              </Button>
            </div>
          </motion.div>

          {/* Media Side */}
          <motion.div
            className="relative"
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={fadeInRight}
          >
            <SharingPermissionsDemo />
          </motion.div>
        </div>
      </Container>
    </section>
  )
}
